import type { UserRole } from "./types";
import { supabase } from "./supabase";

const APP_URLS: Record<string, string> = {
  desktop: import.meta.env.VITE_DESKTOP_APP_URL || "https://app.cradleos.com",
  connect: import.meta.env.VITE_CONNECT_APP_URL || "https://connect.cradleos.com",
};

const ROLE_TO_APP: Record<UserRole, string> = {
  admin: "desktop",
  director: "connect",
  teacher: "connect",
  parent: "connect",
};

/** Returns the correct app URL for a given user role. */
export function getTargetAppUrl(role: UserRole): string {
  return APP_URLS[ROLE_TO_APP[role]];
}

/**
 * Checks if the current origin matches the correct app for a role.
 * Returns true (skip redirect) when:
 * - Running in Electron (file: protocol)
 * - On localhost without cross-app env vars configured
 * - The current origin already matches the target app
 */
export function isCorrectAppForRole(role: UserRole): boolean {
  // Never redirect in Electron
  if (window.location.protocol === "file:") return true;

  const targetUrl = getTargetAppUrl(role);

  // On localhost, skip redirect unless env vars point to other localhost ports
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    try {
      const targetHost = new URL(targetUrl).hostname;
      if (targetHost !== "localhost" && targetHost !== "127.0.0.1") {
        return true; // Target is production URL but we're on localhost — skip
      }
    } catch {
      return true;
    }
  }

  // Compare origins
  try {
    const targetOrigin = new URL(targetUrl).origin;
    return window.location.origin === targetOrigin;
  } catch {
    return true; // Invalid URL — don't redirect
  }
}

/**
 * Transfers the current Supabase session to the correct app for the given role.
 * Constructs a URL with tokens in the hash fragment and redirects.
 * The hash fragment is never sent to the server or logged.
 */
export async function transferSessionToApp(role: UserRole): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    // No session to transfer — redirect to target app's login
    window.location.href = `${getTargetAppUrl(role)}/login`;
    return;
  }

  const targetUrl = getTargetAppUrl(role);
  const hash = `#_at=${encodeURIComponent(session.access_token)}&_rt=${encodeURIComponent(session.refresh_token)}`;

  // Clear local session to prevent redirect loops (tokens still valid for target app)
  await supabase.auth.signOut({ scope: 'local' });

  window.location.href = `${targetUrl}/auth-transfer${hash}`;
}
